(() => {
  if (window.kittenyVideoAutoplayInitialized) return;
  window.kittenyVideoAutoplayInitialized = true;

  const observedVideos = new WeakSet();
  const trackedVideos = new Set();
  const isFittingRoom = window.location.pathname === '/pages/fitting-room';
  let playbackFrame;

  const isVideoVisible = (video) => {
    const rect = video.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    return visibleHeight > 0;
  };

  const syncPlayback = () => {
    playbackFrame = undefined;

    trackedVideos.forEach((video) => {
      const isVisible = !document.hidden && isVideoVisible(video);
      video.dataset.kittenyVisible = isVisible ? 'true' : 'false';

      if (isVisible) {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      } else {
        video.pause();
      }
    });
  };

  const schedulePlaybackSync = () => {
    if (playbackFrame) return;
    playbackFrame = window.requestAnimationFrame(syncPlayback);
  };

  const prepareVideo = (video) => {
    if (observedVideos.has(video)) return;
    observedVideos.add(video);
    trackedVideos.add(video);

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.controls = false;
    video.volume = 0;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');
    video.removeAttribute('controls');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('disableremoteplayback', '');

    let posterCover;
    if (!isFittingRoom && video.poster) {
      const media = video.closest('deferred-media');
      if (media && !media.querySelector('.kitteny-video-poster-cover')) {
        posterCover = document.createElement('span');
        posterCover.className = 'kitteny-video-poster-cover';
        posterCover.style.backgroundImage = `url("${video.poster}")`;
        media.appendChild(posterCover);
      }
    }

    const hidePosterCover = () => posterCover?.classList.add('is-playing');
    video.addEventListener('playing', hidePosterCover);
    video.addEventListener('timeupdate', hidePosterCover, { once: true });
    if (!video.paused) hidePosterCover();

    const preloadObserver = new IntersectionObserver(
      ([entry], observer) => {
        if (!entry.isIntersecting) return;

        video.preload = 'auto';
        if (video.readyState < 2) video.load();
        observer.unobserve(video);
      },
      { rootMargin: '400px 0px', threshold: 0 }
    );

    const playIfVisible = () => {
      if (video.dataset.kittenyVisible !== 'true') return;
      video.muted = true;
      const playPromise = video.play();
      if (playPromise) playPromise.then(hidePosterCover).catch(() => {});
    };

    video.addEventListener('loadedmetadata', playIfVisible);
    video.addEventListener('canplay', playIfVisible);

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        video.dataset.kittenyVisible = isVisible ? 'true' : 'false';

        if (isVisible) {
          if (video.readyState === 0) video.load();
          playIfVisible();
        } else {
          video.pause();
        }
      },
      { threshold: 0 }
    );

    preloadObserver.observe(video);
    observer.observe(video);
  };

  const setupKittenyVideos = (root = document) => {
    const selector = isFittingRoom
      ? 'video.product-media__video'
      : 'product-card video.product-media__video';

    root.querySelectorAll(selector).forEach(prepareVideo);
    schedulePlaybackSync();
  };

  const hidePlayButtons = () => {
    if (document.getElementById('kitteny-video-autoplay-styles')) return;

    const style = document.createElement('style');
    style.id = 'kitteny-video-autoplay-styles';
    style.textContent = `
      product-card deferred-media > button,
      product-card .video-interaction-hint,
      .kitteny-fitting-room .video-interaction-hint {
        display: none !important;
      }

      .kitteny-fitting-room deferred-media > button {
        visibility: hidden !important;
        pointer-events: none !important;
      }

      product-card video::-webkit-media-controls,
      .kitteny-fitting-room video::-webkit-media-controls {
        display: none !important;
      }

      product-card deferred-media {
        position: relative;
      }

      product-card .kitteny-video-poster-cover {
        position: absolute;
        inset: 0;
        z-index: 2;
        background-position: center;
        background-size: cover;
        pointer-events: none;
        transition: opacity 0.1s linear;
      }

      product-card .kitteny-video-poster-cover.is-playing {
        opacity: 0;
        visibility: hidden;
      }
    `;
    document.head.appendChild(style);
  };

  const initialize = () => {
    if (isFittingRoom) document.documentElement.classList.add('kitteny-fitting-room');
    hidePlayButtons();
    setupKittenyVideos();
    [100, 400, 900, 1800, 3000].forEach((delay) => {
      window.setTimeout(syncPlayback, delay);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('visibilitychange', schedulePlaybackSync);
  window.addEventListener('pageshow', schedulePlaybackSync);
  window.addEventListener('resize', schedulePlaybackSync);
  window.addEventListener('scroll', schedulePlaybackSync, { passive: true });
  window.addEventListener('touchstart', syncPlayback, { passive: true, capture: true });
  window.addEventListener('pointerdown', syncPlayback, { passive: true, capture: true });

  new MutationObserver(() => setupKittenyVideos()).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
